import { Component } from '@angular/core';
import { ItemTypes } from '@app/lists/shared/list-detail-management/model/list-detail-management-view.model';
import { TranslateService } from '@ngx-translate/core';
import {
  PanAppState,
  PlatformEnum,
  PlatformService,
  Tracking,
} from '@panamax/app-state';
import {
  SortableSectionHeader,
  SortDirections,
} from '@shared/components/sortable-section/models/sortable-section.model';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { UsfProductCardModeEnum } from '@shared/constants/usf-product-card-mode.enum';
import { EllipsisClickData } from '@shared/models/usf-product-card.model';
import { SellerShowcaseProductCardService } from '@shared/services/product-card/seller-showcase-product-card.service';
import { Subscription } from 'rxjs';
import {
  DivisionalPromotionEventData,
  DivisionalPromotionExpansionClickData,
  DivisionalPromotionFilterType,
  DivisionalPromotionProductClickData,
  DivisionalPromotionSectionHeaders,
  DivisionalPromotionSortingClickData,
  SellerShowcaseUpdates,
  SellerShowcaseViewModel,
} from './models/seller-showcase.model';
import { SellerShowcaseOverlayService } from './services/seller-showcase-overlay.service';
import { SellerShowcaseService } from './services/seller-showcase.service';
import { OrderItemService } from '@shared/services/order-item.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { PdpLnkSrc } from '@shared/constants/pdp-lnksrc.enum';
import { OrderAnalyticsMessages } from '@order/tracking/analytics/order-analytics.constants';
import { Product } from '@shared/models/product.model';
@Component({
  selector: 'app-seller-showcase',
  templateUrl: './seller-showcase.page.html',
  styleUrls: ['./seller-showcase.page.scss'],
})
export class SellerShowcasePage {
  isDescriptionExpanded: boolean;
  promotionsSectionHeaders: SortableSectionHeader[];

  viewModel: SellerShowcaseViewModel;
  sellerShowcaseViewModelSub$: Subscription = new Subscription();
  updates: SellerShowcaseUpdates = {};

  constructor(
    readonly platformService: PlatformService,
    readonly panAppState: PanAppState,
    private translateService: TranslateService,
    private sellerShowcaseService: SellerShowcaseService,
    private sellerShowcaseProductCardService: SellerShowcaseProductCardService,
    private sellerShowcaseOverlayService: SellerShowcaseOverlayService,
    private orderItemService: OrderItemService,
    private productAnalyticsService: ProductAnalyticsService,
  ) {}

  ionViewWillEnter() {
    this.productAnalyticsService.trackSellerIncentivesPageLoad();
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.sellerShowcaseProducts),
    );
    this.isDescriptionExpanded =
      this.platformService.getPlatformType() !== PlatformEnum.mobile;
    this.loadSellerShowcaseViewModel();
    this.createSectionHeaders();
  }

  ionViewWillLeave() {
    this.sellerShowcaseViewModelSub$?.unsubscribe();
    this.updates = {};
  }

  loadSellerShowcaseViewModel() {
    this.sellerShowcaseService.resetSellerShowcase();
    this.sellerShowcaseViewModelSub$ = this.sellerShowcaseService
      .getSellerShowcaseViewModel$()
      .subscribe(viewModel => (this.viewModel = viewModel));
  }

  expandDescription() {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  createSectionHeaders() {
    this.promotionsSectionHeaders = [
      {
        headerName: DivisionalPromotionSectionHeaders.image,
        displayText: ' ',
        sortDirection: null,
        size: 1.5,
      },
      {
        headerName: DivisionalPromotionSectionHeaders.productNumber,
        displayText: this.translateService.instant(
          'i18n.sellerShowcasePage.productNumber',
        ),
        sortDirection: SortDirections.asc,
        size: 1.35,
      },
      {
        headerName: DivisionalPromotionSectionHeaders.productDescription,
        displayText: this.translateService.instant(
          'i18n.sellerShowcasePage.productDescription',
        ),
        sortDirection: SortDirections.asc,
        size: 4,
      },
      {
        headerName: DivisionalPromotionSectionHeaders.brand,
        displayText: this.translateService.instant(
          'i18n.sellerShowcasePage.brand',
        ),
        sortDirection: SortDirections.asc,
        size: 2,
        offset: 0.15,
      },
      {
        headerName: DivisionalPromotionSectionHeaders.quantity,
        displayText: this.translateService.instant(
          'i18n.sellerShowcasePage.quantity',
        ),
        sortDirection: null,
        size: 2.25,
      },
      {
        headerName: DivisionalPromotionSectionHeaders.actions,
        displayText: ' ',
        sortDirection: null,
        size: 0.75,
      },
    ];
  }

  handleEllipsisClick(event: DivisionalPromotionProductClickData) {
    const ellipsisClickData: EllipsisClickData = {
      event: event?.event,
      isTouch: event?.isTouch,
      mode: UsfProductCardModeEnum.defaultSearch,
      itemType: ItemTypes.product,
      product: event?.promotionalProduct?.product,
      pricingDisabled:
        this.viewModel?.selectedCustomer?.ogPrintPriceInd === 'N',
      isSelected: false,
      list: undefined,
    };

    const buttons =
      this.sellerShowcaseProductCardService.constructEllipsisButtons(
        ellipsisClickData,
      );

    this.sellerShowcaseProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  trackProductImageClick(product: Product, resultRank: number) {
    this.productAnalyticsService.trackProductImageClick(
      product?.summary?.divisionNumber,
      product?.productNumber,
      product?.trackingAttributes,
      resultRank,
    );
  }

  handleProductClick(event: DivisionalPromotionProductClickData) {
    if (event?.isImageClick) {
      this.trackProductImageClick(
        event?.promotionalProduct?.product,
        event?.merchRank,
      );
    }
    this.sellerShowcaseProductCardService.routeToPDP(
      event?.promotionalProduct?.productNumber,
      false,
      PdpLnkSrc.sellerIncentives,
    );
  }

  handleSearchWithin(searchKey: string) {
    const newFilters = !!this.updates?.filters
      ? new Map(this.updates?.filters)
      : new Map();
    if (searchKey && searchKey.length > 0) {
      newFilters.set(DivisionalPromotionFilterType.searchWithin, searchKey);
    } else {
      newFilters.delete(DivisionalPromotionFilterType.searchWithin);
    }

    const expansion = new Map(
      this.viewModel?.divisionalPromotions?.map(promotion => [
        promotion?.key,
        true,
      ]),
    );

    this.updates = { ...this.updates, filters: newFilters, expansion };
    this.sellerShowcaseService.setUpdates(this.updates);
  }

  handleUpdateExpansionEvent(event: DivisionalPromotionExpansionClickData) {
    const newExpansion = !!this.updates?.expansion
      ? new Map(this.updates?.expansion)
      : new Map();
    newExpansion.set(
      event.divisionalPromotion?.key,
      event.divisionalPromotion?.isExpanded,
    );
    this.updates = { ...this.updates, expansion: newExpansion };
    this.sellerShowcaseService.setUpdates(this.updates);
  }

  handleUpdateSortEvent(event: DivisionalPromotionSortingClickData) {
    const newSorting = !!this.updates?.sorting
      ? new Map(this.updates?.sorting)
      : new Map();
    newSorting.set(
      event.divisionalPromotion?.key,
      event.divisionalPromotion?.sortBy,
    );
    this.updates = { ...this.updates, sorting: newSorting };
    this.sellerShowcaseService.setUpdates(this.updates);
  }

  handleClearAllFilters() {
    const currentFilters = !!this.updates?.filters
      ? new Map(this.updates?.filters)
      : new Map();
    const newFilters = new Map();
    if (currentFilters.has(DivisionalPromotionFilterType.searchWithin)) {
      newFilters.set(
        DivisionalPromotionFilterType.searchWithin,
        currentFilters.get(DivisionalPromotionFilterType.searchWithin),
      );
    }
    this.updates = { ...this.updates, filters: newFilters };
    this.sellerShowcaseService.setUpdates(this.updates);
  }

  async handleOpenFilterModal() {
    const modal = this.sellerShowcaseOverlayService.openSortModal(
      this.viewModel?.brandsNames,
      this.viewModel?.promoTypes,
      this.viewModel?.exclusiveBrandValues,
      this.viewModel?.productClasses,
      this.updates?.filters,
    );

    (await modal).onDidDismiss().then(response => {
      if (response?.data?.appliedFilters) {
        const currentFilters = !!this.updates?.filters
          ? new Map(this.updates?.filters)
          : new Map();
        const newFilters: Map<DivisionalPromotionFilterType, string> = new Map(
          response?.data?.appliedFilters,
        );

        if (currentFilters.has(DivisionalPromotionFilterType.searchWithin)) {
          newFilters.set(
            DivisionalPromotionFilterType.searchWithin,
            currentFilters.get(DivisionalPromotionFilterType.searchWithin),
          );
        }

        const expansion = new Map(
          this.viewModel?.divisionalPromotions?.map(promotion => [
            promotion?.key,
            true,
          ]),
        );

        this.updates = { ...this.updates, filters: newFilters, expansion };
        this.sellerShowcaseService.setUpdates(this.updates);
      }
    });
  }

  get filtersApplied() {
    return Array.from(this.updates?.filters?.keys() || []).filter(
      key => key !== DivisionalPromotionFilterType.searchWithin,
    ).length;
  }

  createAddToOrderTrackingData(
    qtyChangeEvent: DivisionalPromotionEventData,
  ): Tracking {
    const product = qtyChangeEvent?.promotionalProduct?.product;
    const quantity = qtyChangeEvent?.event?.quantity;
    const quantityUnits = qtyChangeEvent?.event?.isEaches
      ? product?.summary?.eachUom
      : product?.summary?.salesUom;

    return {
      analytics: {
        data: {
          products: [
            {
              attributes: product?.trackingAttributes,
              addMethod: OrderAnalyticsMessages.sellerIncentives,
              divisionApn: `${product?.summary?.divisionNumber}-${product?.productNumber}`,
              quantity,
              quantityUnits,
            },
          ],
        },
      },
    };
  }

  handleQuantityChange(qtyChangeEvent: DivisionalPromotionEventData) {
    this.orderItemService.changeQuantity(
      qtyChangeEvent.event?.productNumber,
      qtyChangeEvent.event?.quantity,
      qtyChangeEvent.event?.isEaches,
      this.createAddToOrderTrackingData(qtyChangeEvent),
    );
  }
}
